import { useCallback, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { PageHeader, PageListContainer } from "../Page";
import { PageEmbedProvider } from "./PageEmbedContext";

export type TabbedPageTab = {
  key: string;
  label: string;
  icon?: ReactNode;
  /** Defaults to true; use it for role-based tabs. */
  visible?: boolean;
  render: () => ReactNode;
};

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  tabs: TabbedPageTab[];
  /** Tab shown when the URL has no (or an unknown) `tab` value; defaults to the first visible tab. */
  defaultTab?: string;
  /** Query parameter that stores the active tab. */
  paramName?: string;
};

/** Visible tabs only, and the tab that is active for a requested key (falls back to default, then first). */
export function resolveActiveTab(
  tabs: TabbedPageTab[],
  requested: string | null,
  defaultTab?: string
): { visible: TabbedPageTab[]; active: TabbedPageTab | undefined } {
  const visible = tabs.filter((tab) => tab.visible !== false);
  const active =
    visible.find((tab) => tab.key === requested) ??
    visible.find((tab) => tab.key === defaultTab) ??
    visible[0];
  return { visible, active };
}

/**
 * Hub page: one title, a row of tabs and the active tab's content. The active tab lives in the URL
 * (`?tab=`) so it survives a reload and can be linked to; only the active tab is mounted.
 * Pages rendered inside hide their own title (see `PageHeader`).
 */
export default function TabbedPage({ title, subtitle, tabs, defaultTab, paramName = "tab" }: Props) {
  const custom = useAppCustomStyles();
  const [params, setParams] = useSearchParams();
  const { visible, active } = useMemo(
    () => resolveActiveTab(tabs, params.get(paramName), defaultTab),
    [tabs, params, paramName, defaultTab]
  );

  const onChange = useCallback(
    (_: unknown, key: string) => {
      // Other tabs' filters do not apply to the new tab, so the query is reset to just the tab.
      setParams({ [paramName]: key });
    },
    [paramName, setParams]
  );

  if (!active) {
    return null;
  }

  return (
    <PageListContainer>
      {/* Page name lives in the navbar chrome; hub only keeps an optional subtitle. */}
      {subtitle != null ? <PageHeader subtitle={subtitle} /> : null}
      <Tabs
        value={active.key}
        onChange={onChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label={typeof title === "string" ? title : undefined}
        sx={custom.common.tabsMarginBottomTwo}
      >
        {visible.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            id={`hub-tab-${tab.key}`}
            aria-controls={`hub-panel-${tab.key}`}
            icon={tab.icon as any}
            iconPosition="start"
            label={tab.label}
          />
        ))}
      </Tabs>
      <Box role="tabpanel" id={`hub-panel-${active.key}`} aria-labelledby={`hub-tab-${active.key}`}>
        <PageEmbedProvider>{active.render()}</PageEmbedProvider>
      </Box>
    </PageListContainer>
  );
}
